import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { getAllowedBoards } from '../utils/roleAccess';
import { motion, AnimatePresence } from 'framer-motion';
import useIsMobile from '../hooks/useIsMobile';
import { FiAlertCircle, FiAlertTriangle, FiCalendar, FiCheckCircle, FiEdit3, FiInfo, FiLink2, FiList, FiTrash2, FiX } from 'react-icons/fi';
import { onKanbanTasksChange, updateKanbanTasks, onKanbanArchivesChange, updateKanbanArchives, onKanbanTemplatesChange, updateKanbanTemplates } from '../services/kanbanService';
import { onUsersChange } from '../services/userService';
import { getUserKey, readScopedStorage, writeScopedStorage } from '../utils/userStorage';

const Analytics = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useI18n();
  const { currentRole, currentUser } = useAuth();
  const isMobile = useIsMobile();
  const isReadOnly = false;

  const [selectedBoard, setSelectedBoard] = useState(null);
  const [drawerContent, setDrawerContent] = useState(null); 
  const [archiveTab, setArchiveTab] = useState('cards'); 
  const [activeMenuColumn, setActiveMenuColumn] = useState(null);
  const [activeCardMenu, setActiveCardMenu] = useState(null);
  const [editingCol, setEditingCol] = useState(null);
  const [editingColText, setEditingColText] = useState("");
  
  // Double Click tahrirlash uchun
  const [editingCardId, setEditingCardId] = useState(null);
  const [editingCardText, setEditingCardText] = useState("");
  const clickTimer = useRef(null);

  const [activeTemplateColumn, setActiveTemplateColumn] = useState(null);
  const [newLocalTemplate, setNewLocalTemplate] = useState("");
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [draggedItem, setDraggedItem] = useState(null);
  const [addingCardColumn, setAddingCardColumn] = useState(null);
  const [newCardText, setNewCardText] = useState('');
  const [newChecklistText, setNewChecklistText] = useState('');

  // KARTOCHKA TAFSILOTLARI OYNASI UCHUN
  const [openedCard, setOpenedCard] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [activePopup, setActivePopup] = useState(null);
  const [datePopupPosition, setDatePopupPosition] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [hoveredIcon, setHoveredIcon] = useState(null);

  const [isEditingModalTitle, setIsEditingModalTitle] = useState(false);
  const [modalTitleText, setModalTitleText] = useState("");

  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const editorRef = useRef(null);
  const savedLinkSelectionRef = useRef(null);

  // Kalendar uchun
  const [calViewDate, setCalViewDate] = useState(new Date());
  const [selDeadlineDate, setSelDeadlineDate] = useState("");
  const [selDeadlineTime, setSelDeadlineTime] = useState("");
  const [deadlineClock, setDeadlineClock] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setDeadlineClock(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const [toasts, setToasts] = useState([]);
  const toastTimerRef = useRef(null);
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToasts([{ id, message, type }]);
    toastTimerRef.current = setTimeout(() => setToasts([]), 2600);
  };

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, isDanger: true });
  const [promptModal, setPromptModal] = useState({ isOpen: false, title: '', placeholder: '', value: '', description: '', isTemplate: false, actionLabel: '', onConfirm: null });

  const customAlert = (title, message) => setAlertModal({ isOpen: true, title, message });
  const customConfirm = (title, message, onConfirm, isDanger = true) => setConfirmModal({ isOpen: true, title, message, onConfirm, isDanger });
  const closePromptModal = () => setPromptModal({ isOpen: false, title: '', placeholder: '', value: '', description: '', isTemplate: false, actionLabel: '', onConfirm: null });

  const allBoards = [
    { id: "dizayn",  title: t('analytics.boards.dizayn') },
    { id: "montaj",  title: t('analytics.boards.montaj') },
    { id: "vfx-ai",  title: t('analytics.boards.vfxAi') },
  ];
  const allowedBoardIds = getAllowedBoards(currentRole);
  const boards = allowedBoardIds
    ? allBoards.filter(b => allowedBoardIds.includes(b.id))
    : allBoards;

  // Agar faqat 1 ta doska ko'rinsa — avtomatik ochish
  useEffect(() => {
    if (boards.length === 1 && !selectedBoard) {
      setSelectedBoard(boards[0].id);
    }
  }, [boards.length]); // eslint-disable-line
  const defaultDays = ["Dushanba", "Seshanba", "Chorshanba", "Payshanba", "Juma", "Shanba & Yakshanba", "Bayram Postlari"];
  const menuColors = ['#3b82f6', '#a855f7', '#22c55e', '#f97316', '#ef4444', '#06b6d4', '#ec4899', '#eab308', '#6366f1', '#14b8a6'];
  const LABEL_COLORS = [
    { id: 'green',  color: '#16a34a' }, { id: 'lime',   color: '#65a30d' },
    { id: 'yellow', color: '#ca8a04' }, { id: 'orange', color: '#ea580c' },
    { id: 'red',    color: '#dc2626' }, { id: 'pink',   color: '#db2777' },
    { id: 'purple', color: '#7c3aed' }, { id: 'blue',   color: '#2563eb' },
    { id: 'sky',    color: '#0891b2' }, { id: 'gray',   color: '#475569' },
  ];

  const statusColors = { 'Ochiq': '#64748b', 'Jarayon': '#3b82f6', 'Tekshiruvda': '#f59e0b', 'Tayyor': '#10b981' };
  const getDeadlinePresentation = (card) => {
    const workflowColor = statusColors[card.status] || statusColors.Ochiq;
    let color = workflowColor;
    let overdue = false;
    if (card.deadline?.date) {
      const [day, month, year] = card.deadline.date.split('.').map(Number);
      const [hour = 18, minute = 0] = (card.deadline.time || '18:00').split(':').map(Number);
      const deadlineDate = new Date(year, month - 1, day, hour, minute);
      const remainingMs = deadlineDate.getTime() - deadlineClock;
      const isValidDeadline = !Number.isNaN(deadlineDate.getTime());
      overdue = isValidDeadline && remainingMs <= 0;

      if (card.status === 'Ochiq' && isValidDeadline && overdue) {
        color = '#991b1b';
      } else if (card.status === 'Ochiq' && isValidDeadline && remainingMs < 24 * 60 * 60 * 1000) {
        const progress = 1 - remainingMs / (24 * 60 * 60 * 1000);
        const toRgb = (hex) => hex.match(/[\da-f]{2}/gi).map(value => parseInt(value, 16));
        const from = toRgb(workflowColor);
        const to = [239, 68, 68];
        const blended = from.map((value, index) => Math.round(value + (to[index] - value) * progress));
        color = `#${blended.map(value => value.toString(16).padStart(2, '0')).join('')}`;
      }
    }
    return {
      color,
      background: `${color}1a`,
      border: `1px solid ${color}66`,
      overdue,
    };
  };
  const MEMBER_ROLE_COLORS = {
    'Admin': '#ef4444', 'SMM Menejer': '#3b82f6', 'Dizayner': '#8b5cf6',
    'Montajor': '#f97316', 'Kopirayter': '#22c55e', 'VFX Mutaxasis': '#06b6d4', 'AI Mutaxasis': '#ec4899',
  };

  const getCurrentUserName = () => {
    if (currentUser?.name) return currentUser.name;
    try { const s = localStorage.getItem(getUserKey('name')); return s ? JSON.parse(s) : 'Admin'; } catch { return 'Admin'; }
  };

  const [allMembers, setAllMembers] = useState([]);
  useEffect(() => {
    const adminEmail = (() => { try { const s = localStorage.getItem(getUserKey('email')); return s ? JSON.parse(s).toLowerCase() : ''; } catch { return ''; } })();
    const unsub = onUsersChange((users, err) => {
      if (err) return;
      setAllMembers(
        users
          .filter(u => (u.email || '').toLowerCase() !== adminEmail)
          .map(u => ({ id: u.id, name: u.name || u.email, color: MEMBER_ROLE_COLORS[u.role] || '#64748b' }))
      );
    });
    return () => unsub();
  }, []); // eslint-disable-line

  const [tasks, setTasks] = useState(() => {
    const initialTasks = { "dizayn": {}, "montaj": {}, "vfx-ai": {} };
    allBoards.forEach(b => { defaultDays.forEach(day => { initialTasks[b.id][day] = []; }); initialTasks[b.id]._order = [...defaultDays]; });
    return initialTasks;
  });

  const [archives, setArchives] = useState(() => ({ columns: [], cards: [] }));
  const [templates, setTemplates] = useState([]);
  const [columnColors, setColumnColors] = useState(() => readScopedStorage('colors', {}));

  // Firebase sync uchun ref'lar
  const firebaseReadyRef = useRef(false);
  const archivesReadyRef = useRef(false);
  const templatesReadyRef = useRef(false);
  const currentTasksRef = useRef(null);
  const savingTasksRef = useRef(false);
  const taskSaveTimerRef = useRef(null);
  const currentArchivesRef = useRef(null);
  const savingArchivesRef = useRef(false);
  const archiveSaveTimerRef = useRef(null);
  const currentTemplatesRef = useRef(null);
  const savingTemplatesRef = useRef(false);
  const templateSaveTimerRef = useRef(null);

  // Real-time: Firebase → local state (tasks)
  useEffect(() => {
    const unsub = onKanbanTasksChange((fbTasks, err) => {
      if (err) {
        showToast('Firebase saqlash xatosi: ' + err.message, 'error');
        return;
      }
      if (fbTasks !== null) {
        firebaseReadyRef.current = true;
        if (!savingTasksRef.current && JSON.stringify(fbTasks) !== JSON.stringify(currentTasksRef.current)) {
          setTasks(fbTasks);
        }
      } else {
        firebaseReadyRef.current = true;
        if (currentTasksRef.current) {
          updateKanbanTasks(currentTasksRef.current).catch((saveError) => {
            showToast('Tasklarni saqlab bo‘lmadi: ' + saveError.message, 'error');
          });
        }
      }
    });
    return unsub;
  }, []); // eslint-disable-line

  // Real-time: Firebase → local state (archives)
  useEffect(() => {
    const unsub = onKanbanArchivesChange((fbArch, err) => {
      if (err) {
        showToast('Arxivni yuklash xatosi: ' + err.message, 'error');
        return;
      }
      archivesReadyRef.current = true;
      if (fbArch === null) {
        if (currentArchivesRef.current) {
          updateKanbanArchives(currentArchivesRef.current).catch((saveError) => {
            showToast('Arxivni saqlab bo‘lmadi: ' + saveError.message, 'error');
          });
        }
        return;
      }
      if (!savingArchivesRef.current && JSON.stringify(fbArch) !== JSON.stringify(currentArchivesRef.current)) {
        setArchives(fbArch);
      }
    });
    return unsub;
  }, []);

  // Real-time: Firebase → local state (templates)
  useEffect(() => {
    const unsub = onKanbanTemplatesChange((fbTemplates, err) => {
      if (err) {
        showToast('Shablonlarni yuklash xatosi: ' + err.message, 'error');
        return;
      }
      templatesReadyRef.current = true;
      if (fbTemplates === null) {
        if (currentTemplatesRef.current) {
          updateKanbanTemplates(currentTemplatesRef.current).catch((saveError) => {
            showToast('Shablonlarni saqlab bo‘lmadi: ' + saveError.message, 'error');
          });
        }
        return;
      }
      if (!savingTemplatesRef.current && JSON.stringify(fbTemplates) !== JSON.stringify(currentTemplatesRef.current)) {
        setTemplates(fbTemplates);
      }
    });
    return unsub;
  }, []);

  // Local state → localStorage + Firebase (tasks, debounced 600ms)
  useEffect(() => {
    currentTasksRef.current = tasks;
    writeScopedStorage('kanban', tasks);
    if (!firebaseReadyRef.current) return;
    savingTasksRef.current = true;
    if (taskSaveTimerRef.current) clearTimeout(taskSaveTimerRef.current);
    taskSaveTimerRef.current = setTimeout(() => {
      updateKanbanTasks(tasks).catch((saveError) => {
        showToast('Tasklarni saqlab bo‘lmadi: ' + saveError.message, 'error');
      }).finally(() => { savingTasksRef.current = false; });
    }, 600);
  }, [tasks]);

  // Local state → localStorage + Firebase (archives, debounced 600ms)
  useEffect(() => {
    currentArchivesRef.current = archives;
    writeScopedStorage('archives', archives);
    if (!archivesReadyRef.current) return;
    savingArchivesRef.current = true;
    if (archiveSaveTimerRef.current) clearTimeout(archiveSaveTimerRef.current);
    archiveSaveTimerRef.current = setTimeout(() => {
      updateKanbanArchives(archives).catch((saveError) => {
        showToast('Arxivni saqlab bo‘lmadi: ' + saveError.message, 'error');
      }).finally(() => { savingArchivesRef.current = false; });
    }, 600);
  }, [archives]);

  // Local state → localStorage + Firebase (templates, globally shared)
  useEffect(() => {
    currentTemplatesRef.current = templates;
    writeScopedStorage('templates', templates);
    if (!templatesReadyRef.current) return;
    savingTemplatesRef.current = true;
    if (templateSaveTimerRef.current) clearTimeout(templateSaveTimerRef.current);
    templateSaveTimerRef.current = setTimeout(() => {
      updateKanbanTemplates(templates).catch((saveError) => {
        showToast('Shablonlarni saqlab bo‘lmadi: ' + saveError.message, 'error');
      }).finally(() => { savingTemplatesRef.current = false; });
    }, 600);
  }, [templates]);

  useEffect(() => { writeScopedStorage('colors', columnColors); }, [columnColors]);

  useEffect(() => {
    if (editorRef.current && openedCard && !isEditingDesc) {
      if (editorRef.current.innerHTML !== (openedCard.card.description || "")) {
        editorRef.current.innerHTML = openedCard.card.description || "";
      }
    }
  }, [openedCard, isEditingDesc]);

  // === FUNKSIYALAR ===
  
  const handleAddColumnSubmit = () => {
    const trimmed = newColumnName.trim();
    if (trimmed !== "") {
      if (tasks[selectedBoard][trimmed]) { customAlert(t('analytics.messages.duplicateColumnTitle'), t('analytics.messages.duplicateColumnMessage')); return; }
      setTasks(prev => {
        const board = prev[selectedBoard];
        const currentOrder = Array.isArray(board._order) ? board._order : Object.keys(board).filter(k => k !== '_order');
        return { ...prev, [selectedBoard]: { ...board, [trimmed]: [], _order: [...currentOrder, trimmed] } };
      });
      showToast(t('analytics.messages.newColumnAdded'), 'success');
      setNewColumnName(""); setIsAddingColumn(false);
    }
  };

  const renameColumn = (oldName, newName) => {
    const trimmed = newName.trim();
    if(trimmed === oldName || trimmed === "") { setEditingCol(null); return; }
    if(tasks[selectedBoard][trimmed]) { customAlert(t('analytics.messages.duplicateColumnTitle'), t('analytics.messages.duplicateColumnMessage')); setEditingCol(null); return; }
    setTasks(prev => {
      const updatedBoard = {};
      Object.keys(prev[selectedBoard]).forEach(key => {
        if (key === '_order') updatedBoard['_order'] = (prev[selectedBoard]._order || []).map(k => k === oldName ? trimmed : k);
        else if (key === oldName) updatedBoard[trimmed] = prev[selectedBoard][oldName];
        else updatedBoard[key] = prev[selectedBoard][key];
      });
      return { ...prev, [selectedBoard]: updatedBoard };
    });
    setColumnColors(prev => {
      const updated = { ...prev };
      if (updated[selectedBoard]?.[oldName]) { updated[selectedBoard][trimmed] = updated[selectedBoard][oldName]; delete updated[selectedBoard][oldName]; }
      return updated;
    });
    showToast(t('analytics.messages.columnRenamed'), 'info');
    setEditingCol(null);
  };

  const updateCardDetails = (day, cardId, newFields, historyMessage = null, historyType = 'event') => {
    setTasks(prev => {
      const boardTasks = { ...prev[selectedBoard] };
      const dayList = [...(boardTasks[day] || [])];
      const cIndex = dayList.findIndex(c => c.id === cardId);
      if (cIndex > -1) {
        const currentCard = dayList[cIndex];
        const updatedCard = { ...currentCard, ...newFields };
        const now = new Date().toISOString();
        const authorName = getCurrentUserName();
        const authorId = currentUser?.id || null;

        if (!updatedCard.createdAt) {
          updatedCard.createdAt = now;
          updatedCard.createdBy = authorId || authorName;
        }
        if (!updatedCard.openedAt) {
          updatedCard.openedAt = updatedCard.createdAt || now;
        }
        updatedCard.updatedAt = now;
        updatedCard.updatedBy = authorId || authorName;

        if (historyMessage) {
          const newEvent = { id: Date.now(), type: historyType, author: authorName, text: historyMessage, date: new Date().toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) };
          updatedCard.history = [newEvent, ...(updatedCard.history || [])];
        }

        if (newFields?.status && newFields.status !== currentCard.status) {
          const statusEvent = {
            status: newFields.status,
            date: now,
            author: authorId || authorName,
            authorName,
          };
          updatedCard.statusHistory = [statusEvent, ...(updatedCard.statusHistory || [])];
        } else if (!updatedCard.statusHistory?.length && updatedCard.status) {
          updatedCard.statusHistory = [{
            status: updatedCard.status,
            date: updatedCard.createdAt || now,
            author: authorId || authorName,
            authorName,
          }];
        }

        dayList[cIndex] = updatedCard;
        boardTasks[day] = dayList;

        if (openedCard && openedCard.card.id === cardId) {
          setOpenedCard({ day, card: updatedCard });
        }
      }
      return { ...prev, [selectedBoard]: boardTasks };
    });
  };

  const addTask = (day) => {
    setAddingCardColumn(day);
    setNewCardText('');
  };

  const submitNewCard = (day) => {
    const text = newCardText.trim();
    if (!text) { setAddingCardColumn(null); return; }
    const now = new Date().toISOString();
    const authorName = getCurrentUserName();
    const newCard = {
      id: Date.now(), text, status: "Ochiq", description: "", deadline: null, assigned: [], labels: [], checklist: [],
      openedAt: now,
      createdAt: now,
      createdBy: currentUser?.id || authorName,
      updatedAt: now,
      updatedBy: currentUser?.id || authorName,
      statusHistory: [{ status: 'Ochiq', date: now, author: currentUser?.id || authorName, authorName }],
      history: [{ id: Date.now(), type: 'event', author: authorName, text: `Vazifani "${day}" ro'yxatiga qo'shdi`, date: new Date().toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }]
    };
    setTasks(prev => ({ ...prev, [selectedBoard]: { ...prev[selectedBoard], [day]: [...(prev[selectedBoard][day] || []), newCard] } }));
    setNewCardText('');
    setAddingCardColumn(null);
    showToast(t('analytics.messages.taskAdded'), 'success');
  };

  const toggleLabel = (labelId) => {
    if (!openedCard) return;
    const current = openedCard.card.labels || [];
    const newLabels = current.includes(labelId) ? current.filter(id => id !== labelId) : [...current, labelId];
    updateCardDetails(openedCard.day, openedCard.card.id, { labels: newLabels });
  };

  const addChecklistItem = (day, cardId) => {
    const text = newChecklistText.trim();
    if (!text) return;
    const newItem = { id: Date.now(), text, done: false };
    setTasks(prev => {
      const boardTasks = { ...prev[selectedBoard] };
      const dayList = [...(boardTasks[day] || [])];
      const idx = dayList.findIndex(c => c.id === cardId);
      if (idx > -1) {
        const card = dayList[idx];
        dayList[idx] = { ...card, checklist: [...(card.checklist || []), newItem] };
        boardTasks[day] = dayList;
        if (openedCard && openedCard.card.id === cardId) setOpenedCard({ day, card: dayList[idx] });
      }
      return { ...prev, [selectedBoard]: boardTasks };
    });
    setNewChecklistText('');
  };

  const toggleChecklistItem = (day, cardId, itemId) => {
    setTasks(prev => {
      const boardTasks = { ...prev[selectedBoard] };
      const dayList = [...(boardTasks[day] || [])];
      const idx = dayList.findIndex(c => c.id === cardId);
      if (idx > -1) {
        const card = dayList[idx];
        dayList[idx] = { ...card, checklist: (card.checklist || []).map(i => i.id === itemId ? { ...i, done: !i.done } : i) };
        boardTasks[day] = dayList;
        if (openedCard && openedCard.card.id === cardId) setOpenedCard({ day, card: dayList[idx] });
      }
      return { ...prev, [selectedBoard]: boardTasks };
    });
  };

  const removeChecklistItem = (day, cardId, itemId) => {
    setTasks(prev => {
      const boardTasks = { ...prev[selectedBoard] };
      const dayList = [...(boardTasks[day] || [])];
      const idx = dayList.findIndex(c => c.id === cardId);
      if (idx > -1) {
        const card = dayList[idx];
        dayList[idx] = { ...card, checklist: (card.checklist || []).filter(i => i.id !== itemId) };
        boardTasks[day] = dayList;
        if (openedCard && openedCard.card.id === cardId) setOpenedCard({ day, card: dayList[idx] });
      }
      return { ...prev, [selectedBoard]: boardTasks };
    });
  };

  const handleCardClick = (e, day, t) => {
    if (editingCardId === t.id) return;
    if (e.detail === 1) {
      clickTimer.current = setTimeout(() => {
        const now = new Date().toISOString();
        const nextCard = { ...t, openedAt: t.openedAt || now, createdAt: t.createdAt || now, updatedAt: now };
        if (!t.openedAt) {
          setTasks(prev => {
            const boardTasks = { ...prev[selectedBoard] };
            const dayList = [...(boardTasks[day] || [])];
            const idx = dayList.findIndex(c => c.id === t.id);
            if (idx === -1) return prev;
            const card = dayList[idx];
            const updatedCard = { ...card, openedAt: now, createdAt: card.createdAt || now, updatedAt: now };
            dayList[idx] = updatedCard;
            boardTasks[day] = dayList;
            return { ...prev, [selectedBoard]: boardTasks };
          });
        }
        setOpenedCard({ day, card: nextCard });
        setIsEditingDesc(false);
      }, 250);
    } else if (e.detail === 2) {
      clearTimeout(clickTimer.current);
      setEditingCardId(t.id);
      setEditingCardText(t.text);
    }
  };

  const saveBoardCardEdit = (day, t) => {
    if (editingCardText.trim() !== "" && editingCardText !== t.text) {
      updateCardDetails(day, t.id, { text: editingCardText }, `${t('analytics.messages.taskRenamed')}: «${editingCardText}»`);
      showToast(t('analytics.messages.taskRenamed'), "info");
    }
    setEditingCardId(null);
  };

  const handleSaveModalTitle = () => {
    if (openedCard && modalTitleText.trim() !== "" && modalTitleText !== openedCard.card.text) {
      updateCardDetails(openedCard.day, openedCard.card.id, { text: modalTitleText }, `${t('analytics.messages.titleUpdated')}: «${modalTitleText}»`);
      showToast(t('analytics.messages.titleUpdated'), "info");
    }
    setIsEditingModalTitle(false);
  };

  const normalizeLinkUrl = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const value = raw.trim();
    if (!value) return '';

    const lower = value.toLowerCase();
    const jsPrefix = String.fromCharCode(106, 97, 118, 97, 115, 99, 114, 105, 112, 116, 58);
    if (lower.startsWith(jsPrefix) || lower.startsWith('data:')) return '';
    if (/^(https?:)?\/\//i.test(value) || /^mailto:/i.test(value) || /^tel:/i.test(value)) {
      return value;
    }
    return `https://${value}`;
  };

  const sanitizeDescriptionLinks = (html) => {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const textNodes = [];

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const value = node.nodeValue || '';
        if (value.trim()) textNodes.push(node);
      }
      node.childNodes.forEach(walk);
    };

    walk(doc.body);

    textNodes.forEach((node) => {
      const value = node.nodeValue || '';
      const matches = value.match(/https?:\/\/[^\s<>()]+/gi) || [];
      if (!matches.length) return;

      const fragment = doc.createDocumentFragment();
      let lastIndex = 0;
      matches.forEach((match) => {
        const index = value.indexOf(match, lastIndex);
        if (index > lastIndex) {
          fragment.appendChild(doc.createTextNode(value.slice(lastIndex, index)));
        }

        const link = doc.createElement('a');
        const clean = normalizeLinkUrl(match);
        if (!clean) {
          fragment.appendChild(doc.createTextNode(match));
        } else {
          link.href = clean;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.textContent = match;
          link.style.color = 'inherit';
          link.style.textDecoration = 'underline';
          link.style.textDecorationThickness = '1.5px';
          link.style.fontWeight = '500';
          fragment.appendChild(link);
        }
        lastIndex = index + match.length;
      });

      if (lastIndex < value.length) {
        fragment.appendChild(doc.createTextNode(value.slice(lastIndex)));
      }

      const parent = node.parentNode;
      if (parent) {
        parent.replaceChild(fragment, node);
      }
    });

    const anchors = doc.querySelectorAll('a[href]');
    anchors.forEach((anchor) => {
      const clean = normalizeLinkUrl(anchor.getAttribute('href'));
      if (!clean) {
        anchor.removeAttribute('href');
        return;
      }
      anchor.setAttribute('href', clean);
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
      anchor.style.color = 'inherit';
      anchor.style.textDecoration = 'underline';
      anchor.style.textDecorationThickness = '1.5px';
      anchor.style.fontWeight = '500';
    });

    return doc.body.innerHTML;
  };

  const handleSaveDescription = () => {
    if (openedCard && editorRef.current) {
      const rawHtml = editorRef.current.innerHTML;
      const normalizedHtml = sanitizeDescriptionLinks(rawHtml);
      updateCardDetails(openedCard.day, openedCard.card.id, { description: normalizedHtml }, t('analytics.messages.descriptionSaved'));
      setIsEditingDesc(false);
      showToast(t('analytics.messages.descriptionSaved'), "success");
    }
  };

  const openExternalLink = (url) => {
    const safeUrl = normalizeLinkUrl(url);
    if (!safeUrl) return;
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEditorClick = (e) => {
    const anchor = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!isEditingDesc && anchor) {
      e.preventDefault();
      openExternalLink(anchor.getAttribute('href') || anchor.href);
      return;
    }
    if (!isEditingDesc && !isReadOnly) {
      setIsEditingDesc(true);
    }
  };

  const handleAddComment = () => {
    if (!commentText.trim() || !openedCard) return;
    updateCardDetails(openedCard.day, openedCard.card.id, {}, commentText, 'comment');
    setCommentText("");
    showToast("Kommentariya qo'shildi.", 'success');
  };

  const handleStatusChange = (newStatus) => {
    updateCardDetails(openedCard.day, openedCard.card.id, { status: newStatus }, `${t('analytics.messages.statusChanged')}: ${newStatus}`);
    setActivePopup(null);
    showToast(t('analytics.messages.statusChanged'), "info");
  };

  const toggleMember = (memberId) => {
    const currentAssigned = openedCard.card.assigned || [];
    const isAdding = !currentAssigned.includes(memberId);
    const newAssigned = isAdding ? [...currentAssigned, memberId] : currentAssigned.filter(id => id !== memberId);
    const memberName = allMembers.find(m => m.id === memberId)?.name;
    
    updateCardDetails(openedCard.day, openedCard.card.id, { assigned: newAssigned }, `${isAdding ? t('analytics.messages.membersUpdated') : t('analytics.messages.membersUpdated')}: ${memberName}`);
    showToast(t('analytics.messages.membersUpdated'), 'success');
  };

  const getCardOpenedDateString = (card) => {
    const value = card?.openedAt || card?.createdAt;
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };

  const openDatePopup = (event) => {
    const willOpen = activePopup !== 'date';
    if (willOpen && event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const popupWidth = Math.min(292, window.innerWidth - 32);
      const popupHeight = 500;
      const gap = 8;
      const top = window.innerHeight - rect.bottom >= popupHeight + gap
        ? rect.bottom + gap
        : Math.max(16, rect.top - popupHeight - gap);
      const left = Math.min(Math.max(16, rect.left), window.innerWidth - popupWidth - 16);
      setDatePopupPosition({ top, left });
    }
    if (openedCard.card.deadline && typeof openedCard.card.deadline === 'object') {
      const dateString = openedCard.card.deadline.date || "";
      const timeString = openedCard.card.deadline.time || "";
      const parts = typeof dateString === 'string' ? dateString.split('.') : [];
      if (parts.length === 3) setCalViewDate(new Date(parts[2], parts[1] - 1, parts[0]));
      else setCalViewDate(new Date());
      setSelDeadlineDate(dateString);
      setSelDeadlineTime(timeString || "18:00");
    } else {
      const now = new Date();
      const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const dd = String(nextDay.getDate()).padStart(2, '0');
      const mm = String(nextDay.getMonth() + 1).padStart(2, '0');
      setCalViewDate(nextDay);
      setSelDeadlineDate(`${dd}.${mm}.${nextDay.getFullYear()}`);
      setSelDeadlineTime("18:00");
    }
    setActivePopup(activePopup === 'date' ? null : 'date');
  };

  const saveDeadline = () => {
    if (selDeadlineDate && selDeadlineDate === getCardOpenedDateString(openedCard.card)) {
      showToast('Kartochka ochilgan kunni deadline qilib belgilab bo‘lmaydi', 'error');
      return;
    }
    if (!selDeadlineDate) {
      updateCardDetails(openedCard.day, openedCard.card.id, { deadline: null }, t('analytics.messages.deadlineRemoved'));
      showToast(t('analytics.messages.deadlineRemoved'), "info");
    } else {
      updateCardDetails(openedCard.day, openedCard.card.id, { deadline: { date: selDeadlineDate, time: selDeadlineTime || "18:00" } }, `${t('analytics.messages.deadlineSaved')}: ${selDeadlineDate} ${selDeadlineTime || "18:00"}`);
      showToast(t('analytics.messages.deadlineSaved'), "success");
    }
    setActivePopup(null);
  };

  const changeMonth = (offset) => { setCalViewDate(new Date(calViewDate.getFullYear(), calViewDate.getMonth() + offset, 1)); };
  const getCalendarDays = () => {
    const year = calViewDate.getFullYear(), month = calViewDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let firstDay = new Date(year, month, 1).getDay(); 
    if (firstDay === 0) firstDay = 7; 
    const days = [];
    for (let i = 1; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };
  const monthNames = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

  const execCmd = (cmd, value = null) => { document.execCommand(cmd, false, value); };
  const handleLinkInsertion = () => {
    const selection = window.getSelection();
    savedLinkSelectionRef.current = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
    setPromptModal({
      isOpen: true,
      title: t('analytics.insertLink'),
      placeholder: t('analytics.messages.linkUrlPrompt'),
      value: 'https://',
      description: '',
      isTemplate: false,
      actionLabel: t('analytics.buttons.send'),
      onConfirm: (url) => {
        const cleanUrl = normalizeLinkUrl(url);
        if (!cleanUrl) {
          customAlert(t('analytics.insertLink'), t('analytics.messages.invalidLink'));
          return;
        }
        const editor = editorRef.current;
        if (!editor || !savedLinkSelectionRef.current) return;
        editor.focus();
        const currentSelection = window.getSelection();
        currentSelection.removeAllRanges();
        currentSelection.addRange(savedLinkSelectionRef.current);
        execCmd('createLink', cleanUrl);
        savedLinkSelectionRef.current = null;
      },
    });
  };

  const handleDropCard = (targetCol, targetCardId) => {
    if (!draggedItem || draggedItem.type !== 'CARD') return;
    const { id, sourceCol } = draggedItem;
    if (sourceCol === targetCol && id === targetCardId) return;

    setTasks(prev => {
      const boardTasks = { ...prev[selectedBoard] };
      const sourceList = [...(boardTasks[sourceCol] || [])];
      const cardIndex = sourceList.findIndex(c => c.id === id);
      if (cardIndex === -1) return prev;
      
      const card = sourceList[cardIndex];
      sourceList.splice(cardIndex, 1);
      boardTasks[sourceCol] = sourceList;

      const targetList = [...(boardTasks[targetCol] || [])];
      if (targetCardId) {
        const tIndex = targetList.findIndex(c => c.id === targetCardId);
        targetList.splice(tIndex, 0, card); 
      } else { targetList.push(card); }
      boardTasks[targetCol] = targetList;
      return { ...prev, [selectedBoard]: boardTasks };
    });
    
    if (sourceCol !== targetCol) {
       updateCardDetails(targetCol, id, {}, `"${sourceCol}" → "${targetCol}" ga ko'chirildi`);
       showToast(t('analytics.messages.taskMoved'), 'info');
    }
  };

  const archiveTask = (day, task) => {
    customConfirm(t('analytics.messages.archiveCardConfirmTitle'), t('analytics.messages.archiveCardConfirmMessage').replace('{name}', task.text), () => {
      setArchives(prev => ({ ...prev, cards: [...prev.cards, { ...task, originalDay: day, board: selectedBoard }] }));
      setTasks(prev => ({ ...prev, [selectedBoard]: { ...prev[selectedBoard], [day]: prev[selectedBoard][day].filter(t => t.id !== task.id) } }));
      showToast(t('analytics.messages.cardArchived'), 'warning');
      if (openedCard && openedCard.card.id === task.id) setOpenedCard(null); 
    }, false);
    setActiveCardMenu(null);
  };

  const saveCardAsTemplate = (card) => {
    setTemplates(prev => [...prev, {
      id: Date.now(),
      text: card.text || '',
      description: card.description || '',
      labels: Array.isArray(card.labels) ? [...card.labels] : [],
      checklist: Array.isArray(card.checklist) ? card.checklist.map(item => ({ ...item, done: false })) : [],
    }]);
    showToast(t('analytics.toast.templateSaved'), 'success');
    setActiveCardMenu(null);
  };

  const archiveColumn = (day) => {
    customConfirm(t('analytics.messages.archiveColumnConfirmTitle'), t('analytics.messages.archiveColumnConfirmMessage').replace('{name}', day), () => {
      setArchives(prev => ({ ...prev, columns: [...prev.columns, { id: Date.now(), name: day, tasks: tasks[selectedBoard][day] || [], board: selectedBoard, color: columnColors?.[selectedBoard]?.[day] }] }));
      setTasks(prev => {
        const board = { ...prev[selectedBoard] };
        delete board[day];
        board._order = (Array.isArray(board._order) ? board._order : []).filter(k => k !== day);
        return { ...prev, [selectedBoard]: board };
      });
      showToast(t('analytics.messages.columnArchived'), 'warning');
    });
    setActiveMenuColumn(null);
  };

  const archiveAllCardsInColumn = (day) => {
    const cardsToArchive = tasks[selectedBoard][day] || [];
    if (cardsToArchive.length === 0) { customAlert(t('analytics.messages.noCardsToArchiveTitle'), t('analytics.messages.noCardsToArchiveMessage')); return; }
    customConfirm(t('analytics.messages.archiveAllCardsConfirmTitle'), t('analytics.messages.archiveAllCardsConfirmMessage').replace('{name}', day), () => {
      const newArchiveCards = cardsToArchive.map(t => ({ ...t, originalDay: day, board: selectedBoard }));
      setArchives(prev => ({ ...prev, cards: [...prev.cards, ...newArchiveCards] }));
      setTasks(prev => ({ ...prev, [selectedBoard]: { ...prev[selectedBoard], [day]: [] } }));
      showToast(t('analytics.messages.allTasksArchived'), 'warning');
    });
    setActiveMenuColumn(null);
  };

  const changeColumnColor = (day, colorHex) => {
    setColumnColors(prev => { const updated = { ...prev }; if (!updated[selectedBoard]) updated[selectedBoard] = {}; updated[selectedBoard][day] = colorHex; return updated; });
  };

  const restoreCard = (card) => {
    setTasks(prev => ({ ...prev, [card.board]: { ...prev[card.board], [card.originalDay]: [...(prev[card.board][card.originalDay] || []), { id: card.id, text: card.text, status: card.status }] } }));
    setArchives(prev => ({ ...prev, cards: prev.cards.filter(c => c.id !== card.id) }));
    showToast(`Arxivdan tiklandi`, 'success');
  };

  const restoreColumn = (col) => {
    setTasks(prev => {
      const dayKey = prev[col.board][col.name] ? `${col.name} (Tiklandi)` : col.name;
      const board = prev[col.board];
      const currentOrder = Array.isArray(board._order) ? board._order : Object.keys(board).filter(k => k !== '_order');
      return { ...prev, [col.board]: { ...board, [dayKey]: col.tasks || [], _order: [...currentOrder, dayKey] } };
    });
    if(col.color) { setColumnColors(prev => { const updated = { ...prev }; if (!updated[col.board]) updated[col.board] = {}; updated[col.board][col.name] = col.color; return updated; }); }
    setArchives(prev => ({ ...prev, columns: prev.columns.filter(c => c.id !== col.id) }));
    showToast(t('analytics.toast.columnRestored'), 'success');
  };

  const applyTemplateToColumn = (day, tempText) => {
    const template = typeof tempText === 'string' ? { text: tempText } : tempText;
    const newCard = { 
      id: Date.now(), text: template.text || '', status: "Ochiq", description: template.description || '',
      labels: Array.isArray(template.labels) ? [...template.labels] : [],
      checklist: Array.isArray(template.checklist) ? template.checklist.map(item => ({ ...item, done: false })) : [],
      history: [{ id: Date.now(), type: 'event', author: 'Siz', text: t('analytics.templateAppliedHistory'), date: new Date().toLocaleString('ru-RU') }]
    };
    setTasks(prev => ({ ...prev, [selectedBoard]: { ...prev[selectedBoard], [day]: [...(prev[selectedBoard][day] || []), newCard] } }));
    setActiveTemplateColumn(null);
    showToast(t('analytics.toast.templateApplied'), 'success');
  };

  const createNewTemplate = () => {
    setPromptModal({
      isOpen: true,
      title: t('analytics.newTemplatePromptTitle'),
      placeholder: t('analytics.newTemplatePromptBody'),
      value: '',
      description: '',
      isTemplate: true,
      onConfirm: (text, description) => {
        if (text.trim()) {
          setTemplates(prev => [...prev, { id: Date.now(), text, description: description || '', labels: [], checklist: [] }]);
          showToast(t('analytics.toast.templateCreated'), 'success');
        }
      },
    });
  };

  const editTemplate = (template) => {
    const current = typeof template === 'string' ? { text: template, description: '' } : template;
    setPromptModal({
      isOpen: true,
      title: 'Shablonni tahrirlash',
      placeholder: 'Shablon nomi yoki matni...',
      value: current.text || '',
      description: current.description || '',
      isTemplate: true,
      onConfirm: (text, description) => {
        if (!text.trim()) return;
        setTemplates(prev => prev.map(item => item.id === current.id
          ? { ...item, text, description: description || '' }
          : item
        ));
        showToast('Shablon yangilandi', 'success');
      },
    });
  };

  const createNewLocalTemplate = () => {
    if(newLocalTemplate.trim() !== "") {
      setTemplates(prev => [...prev, { id: Date.now(), text: newLocalTemplate }]);
      setNewLocalTemplate("");
      showToast(t('analytics.toast.templateSaved'), 'success');
    }
  };

  const containerVariants = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 30, scale: 0.95 }, show: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 24 } } };

  // ===============================================
  // BO'LIM TANLASH
  // ===============================================
  if (!selectedBoard) {
    return (
    <motion.div initial="hidden" animate="show" exit={{opacity: 0}} variants={containerVariants} style={{ padding: isMobile ? '16px' : '40px', minHeight: '100vh', background: isLight ? '#f1f5f9' : '#0f172a', color: isLight ? '#0f172a' : '#fff', transition: 'background 0.3s' }}>
      <motion.h1 variants={itemVariants} style={{ fontSize: isMobile ? '22px' : '32px', fontWeight: 'bold', marginBottom: isMobile ? '20px' : '40px' }}>{t('analytics.pickBoard')}</motion.h1>
        <motion.div variants={containerVariants} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: isMobile ? '12px' : '30px' }}>
          {boards.map((board) => (
            <motion.div variants={itemVariants} key={board.id} whileHover={{ scale: 1.03, y: -10 }} whileTap={{ scale: 0.98 }} onClick={() => setSelectedBoard(board.id)} style={{ width: '100%', height: isMobile ? '100px' : '200px', padding: isMobile ? '20px' : '40px', borderRadius: '20px', cursor: 'pointer', background: isLight ? '#fff' : 'rgba(255, 255, 255, 0.08)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', boxShadow: isLight ? '0 10px 25px rgba(0,0,0,0.05)' : '0 15px 30px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)' }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px', fontWeight: '800', letterSpacing: '1px' }}>{board.title}</h2>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    );
  }

  const activeBoardTitle = boards.find(b => b.id === selectedBoard)?.title;
  const getOrderedCols = (boardId) => {
    const board = tasks[boardId] || {};
    const order = board._order;
    if (Array.isArray(order) && order.length) return order.filter(k => k in board && k !== '_order');
    return Object.keys(board).filter(k => k !== '_order');
  };
  const currentColumns = getOrderedCols(selectedBoard);

  return (
    <motion.div 
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
      style={{ padding: isMobile ? '12px' : '30px', height: isMobile ? 'auto' : '100vh', minHeight: '100vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', background: isLight ? '#f1f5f9' : '#0f172a', color: isLight ? '#0f172a' : '#fff', transition: 'background 0.3s ease', position: 'relative', overflow: isMobile ? 'visible' : 'hidden' }}
      onClick={() => { setActiveMenuColumn(null); setActiveCardMenu(null); setActiveTemplateColumn(null); setActivePopup(null); }} 
    >
      
      {/* HEADER */}
      <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', position: 'sticky', top: isMobile ? '-12px' : '-30px', zIndex: 30, background: isLight ? 'rgba(241, 245, 249, 0.9)' : 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(10px)', paddingTop: isMobile ? '12px' : '30px', paddingBottom: '20px', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '20px' }}>
          <button onClick={() => setSelectedBoard(null)} style={{ padding: isMobile ? '8px 12px' : '10px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.05)', color: isLight ? '#0f172a' : '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', fontSize: isMobile ? '13px' : '14px' }}>← {t('analytics.back')}</button>
          <h1 style={{ fontSize: isMobile ? '18px' : '28px', fontWeight: 'bold', margin: 0 }}>{activeBoardTitle} {t('analytics.board')}</h1>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {!isReadOnly && <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={(e) => { e.stopPropagation(); setDrawerContent('templates'); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: isMobile ? '8px 12px' : '10px 20px', borderRadius: '10px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)', color: isLight ? '#475569' : '#cbd5e1', fontWeight: '600', fontSize: isMobile ? '12px' : '14px', cursor: 'pointer' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            {!isMobile && t('analytics.templates')}
          </motion.button>}
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={(e) => { e.stopPropagation(); setDrawerContent('archives'); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: isMobile ? '8px 12px' : '10px 20px', borderRadius: '10px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)', color: isLight ? '#475569' : '#cbd5e1', fontWeight: '600', fontSize: isMobile ? '12px' : '14px', cursor: 'pointer' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>
            {!isMobile && t('analytics.archives')}
          </motion.button>
        </div>
      </div>

      {/* USTUNLAR QISMI */}
      <div className="kanban-scroll" style={{ flex: 1, overflowX: 'auto', display: 'flex', gap: '15px', alignItems: 'flex-start', paddingBottom: isMobile ? '90px' : '20px', WebkitOverflowScrolling: 'touch' }}>
        <AnimatePresence>
          {currentColumns.map(day => {
            const colColor = columnColors?.[selectedBoard]?.[day];

            return (
            <motion.div 
              layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              key={day} draggable={!isReadOnly && editingCardId === null} onDragStart={(e) => { e.stopPropagation(); setDraggedItem({ type: 'COLUMN', name: day }); }} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (draggedItem?.type === 'COLUMN' && draggedItem.name !== day) {
                  setTasks(prev => {
                    const board = prev[selectedBoard];
                    const order = Array.isArray(board._order) ? [...board._order] : Object.keys(board).filter(k => k !== '_order');
                    const oldIndex = order.indexOf(draggedItem.name);
                    const newIndex = order.indexOf(day);
                    order.splice(oldIndex, 1);
                    order.splice(newIndex, 0, draggedItem.name);
                    return { ...prev, [selectedBoard]: { ...board, _order: order } };
                  });
                  showToast(t('analytics.toast.columnReordered'), 'info');
                } else if (draggedItem?.type === 'CARD') { handleDropCard(day, null); }
                setDraggedItem(null);
              }}
              style={{ minWidth: '280px', minHeight: '75vh', padding: '20px', borderRadius: '16px', background: isLight ? '#f8fafc' : '#272844', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)', borderTop: colColor ? `4px solid ${colColor}` : (isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)'), display: 'flex', flexDirection: 'column', position: 'relative', cursor: 'grab', opacity: draggedItem?.type === 'COLUMN' && draggedItem.name === day ? 0.5 : 1 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  {editingCol === day ? (
                    <input autoFocus value={editingColText} onChange={(e) => setEditingColText(e.target.value)} onBlur={() => renameColumn(day, editingColText)} onKeyDown={(e) => e.key === 'Enter' && renameColumn(day, editingColText)} style={{ width: '100%', padding: '5px', borderRadius: '6px', border: '1px solid #3b82f6', background: isLight?'#fff':'#1e293b', color: isLight?'#0f172a':'#fff', fontSize: '14px', fontWeight: 'bold' }} onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }} />
                  ) : (
                    <h3 onDoubleClick={isReadOnly ? undefined : (e) => { e.stopPropagation(); setEditingCol(day); setEditingColText(day); }} title={isReadOnly ? undefined : t('analytics.editColumnHint')} style={{ margin: 0, fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: '800', color: isLight ? '#0f172a' : '#ffffff', cursor: isReadOnly ? 'default' : 'text' }}>{day}</h3>
                  )}
                  <motion.span layout style={{ fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)', color: isLight ? '#475569' : '#cbd5e1', width: '24px', height: '24px', borderRadius: '50%' }}>{tasks[selectedBoard]?.[day]?.length || 0}</motion.span>
                </div>
                {!isReadOnly && <button onClick={(e) => { e.stopPropagation(); setActiveMenuColumn(activeMenuColumn === day ? null : day); }} style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', cursor: 'pointer', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.05)', color: isLight ? '#475569' : '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"></circle><circle cx="12" cy="12" r="2"></circle><circle cx="19" cy="12" r="2"></circle></svg></button>}
              </div>

              {/* Ustun Menyusi */}
              <AnimatePresence>
                {activeMenuColumn === day && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '55px', right: '10px', width: '260px', zIndex: 50, background: isLight ? '#ffffff' : '#1e293b', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.4)', padding: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: isLight ? '#0f172a' : '#fff' }}>{t('analytics.columnActions')}</span>
                      <button onClick={() => setActiveMenuColumn(null)} style={{ background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', fontSize: '18px' }}>×</button>
                    </div>
                    <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '8px', marginBottom: '15px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.03)' }}>
                      <span style={{ fontSize: '11px', color: '#8b94a8', display: 'block', marginBottom: '10px', fontWeight: '600' }}>{t('analytics.changeColumnColor')}</span>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                        <button onClick={() => changeColumnColor(day, null)} style={{ height: '32px', borderRadius: '6px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#475569' : '#cbd5e1'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg></button>
                        {menuColors.map(c => ( <button key={c} onClick={() => changeColumnColor(day, c)} style={{ height: '32px', borderRadius: '6px', background: c, border: 'none', cursor: 'pointer' }}></button> ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <button onClick={() => archiveAllCardsInColumn(day)} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '10px', background: 'transparent', border: 'none', color: isLight ? '#0f172a' : '#cbd5e1', fontSize: '13px', textAlign: 'left', cursor: 'pointer', borderRadius: '6px' }} onMouseOver={e=>e.currentTarget.style.background=isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>{t('analytics.archiveAllCards')}</button>
                      <button onClick={() => archiveColumn(day)} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '10px', background: 'transparent', border: 'none', color: isLight ? '#0f172a' : '#cbd5e1', fontSize: '13px', textAlign: 'left', cursor: 'pointer', borderRadius: '6px' }} onMouseOver={e=>e.currentTarget.style.background=isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>{t('analytics.archiveColumn')}</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <div className="kanban-column-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', scrollbarGutter: 'stable both-edges' }}>
                <AnimatePresence>
                  {(tasks[selectedBoard]?.[day] || []).map(card => (
                    <motion.div
                      layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                      key={card.id} whileHover={{ scale: 1.02 }} draggable={!isReadOnly && editingCardId !== card.id} onDragStart={(e) => { e.stopPropagation(); setDraggedItem({ type: 'CARD', id: card.id, sourceCol: day }); }} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (draggedItem?.type === 'CARD') { handleDropCard(day, card.id); } setDraggedItem(null); }}
                      onClick={(e) => handleCardClick(e, day, card)}
                      style={{ padding: '15px', background: isLight ? '#ffffff' : 'rgba(255,255,255,0.06)', borderRadius: '12px', fontSize: '14px', position: 'relative', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.02)', cursor: editingCardId === card.id ? 'text' : 'pointer', opacity: draggedItem?.type === 'CARD' && draggedItem.id === card.id ? 0.5 : 1 }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        
                        {/* DOSKADAGI KARTOCHKA DIZAYNI */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Rangli status indikatori */}
                            <div title={getDeadlinePresentation(card).overdue ? 'Muddat o\'tib ketgan' : card.status} style={{ width: '10px', height: '10px', borderRadius: '50%', background: getDeadlinePresentation(card).color, flexShrink: 0, transition: 'background 0.3s' }}></div>

                            {editingCardId === card.id ? (
                              <input
                                autoFocus value={editingCardText} onChange={e => setEditingCardText(e.target.value)}
                                onBlur={() => saveBoardCardEdit(day, card)} onKeyDown={e => { if (e.key === 'Enter') saveBoardCardEdit(day, card); }}
                                onClick={e => e.stopPropagation()} onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #3b82f6', borderRadius: '6px', background: isLight ? '#f8fafc' : '#1e293b', color: isLight ? '#0f172a' : '#fff', outline: 'none' }}
                              />
                            ) : (
                              <span style={{ fontWeight: '500', lineHeight: '1.4', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{card.text}</span>
                            )}
                          </div>
                          
                          {/* Teglar */}
                          {card.labels && card.labels.length > 0 && editingCardId !== card.id && (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', paddingLeft: '18px' }}>
                              {card.labels.map(labelId => {
                                const lbl = LABEL_COLORS.find(l => l.id === labelId);
                                return lbl ? <span key={labelId} style={{ height: '8px', minWidth: '40px', borderRadius: '4px', background: lbl.color, display: 'inline-block' }} /> : null;
                              })}
                            </div>
                          )}

                          {/* Srok va ishtirokchilar ikonkalari */}
                          {(card.deadline || (card.assigned && card.assigned.length > 0)) && editingCardId !== card.id && (
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', paddingLeft: '18px' }}>
                              {card.deadline && (
                                (() => {
                                  const deadlineText = `${card.deadline.date}${card.deadline.time ? ` ${card.deadline.time}` : ''}`;
                                  return (
                                    <span title={`Muddat: ${deadlineText}`} style={{ fontSize: '11px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.1)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.12)', padding: '2px 6px', borderRadius: '4px', color: isLight ? '#475569' : '#cbd5e1', fontWeight: 700 }}>
                                      {deadlineText}
                                    </span>
                                  );
                                })()
                              )}
                              {card.assigned && card.assigned.length > 0 && (
                                <div style={{ display: 'flex', marginLeft: 'auto' }}>
                                  {card.assigned.map(id => {
                                    const u = allMembers.find(m => m.id === id);
                                    return u ? (
                                      <div key={u.id} title={u.name} style={{ width: '20px', height: '20px', borderRadius: '50%', background: u.color, color: '#fff', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '-5px', border: `1px solid ${isLight?'#fff':'#272844'}` }}>
                                        {u.name.charAt(0).toUpperCase()}
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {card.checklist && card.checklist.length > 0 && editingCardId !== card.id && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingLeft: '18px', marginTop: '2px' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#64748b' : '#8b94a8'} strokeWidth="2.5"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                            <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#8b94a8' }}>{card.checklist.filter(i => i.done).length}/{card.checklist.length}</span>
                          </div>
                        )}

                        {editingCardId !== card.id && !isReadOnly && (
                          <button onClick={(e) => { e.stopPropagation(); setActiveCardMenu(activeCardMenu === card.id ? null : card.id); }} style={{ background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', padding: '0 5px' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"></circle><circle cx="12" cy="12" r="2"></circle><circle cx="19" cy="12" r="2"></circle></svg></button>
                        )}
                      </div>

                      <AnimatePresence>
                        {activeCardMenu === card.id && (
                          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '35px', right: '5px', width: '200px', zIndex: 60, background: isLight ? '#ffffff' : '#334155', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.3)', padding: '5px' }}>
                            <button onClick={(e) => { e.stopPropagation(); archiveTask(day, card); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: 'transparent', border: 'none', color: isLight ? '#ef4444' : '#f87171', cursor: 'pointer', borderRadius: '4px', textAlign: 'left', fontSize: '13px', fontWeight: '500' }} onMouseOver={e=>e.currentTarget.style.background=isLight ? '#fee2e2' : 'rgba(239,68,68,0.1)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg> {t('analytics.archiveTask')}</button>
                            <button onClick={(e) => { e.stopPropagation(); saveCardAsTemplate(card); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: 'transparent', border: 'none', color: isLight ? '#3b82f6' : '#60a5fa', cursor: 'pointer', borderRadius: '4px', textAlign: 'left', fontSize: '13px', fontWeight: '500', marginTop: '2px' }} onMouseOver={e=>e.currentTarget.style.background=isLight ? '#eff6ff' : 'rgba(59,130,246,0.14)' } onMouseOut={e=>e.currentTarget.style.background='transparent'}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg> {t('analytics.makeTemplate')}</button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <AnimatePresence>
                {activeTemplateColumn === day && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: '65px', left: '10px', right: '10px', zIndex: 40, background: isLight ? '#ffffff' : '#1e293b', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', boxShadow: '0 -5px 20px rgba(0,0,0,0.3)', padding: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{t('analytics.templateListTitle')}</span>
                      <button onClick={() => setActiveTemplateColumn(null)} style={{ background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', fontSize: '18px' }}>×</button>
                    </div>
                    <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' }}>
                      {templates.length === 0 && <span style={{fontSize:'12px', color:'#8b94a8'}}>{t('analytics.noTemplates')}</span>}
                      {templates.map(tmpl => (
                          <div key={tmpl.id} style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <button onClick={() => applyTemplateToColumn(day, tmpl)} style={{ flex: 1, textAlign: 'left', padding: '10px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: isLight ? '#0f172a' : '#fff', fontSize: '13px', cursor: 'pointer', transition: 'background 0.2s', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} onMouseOver={e=>e.currentTarget.style.background=isLight?'#e2e8f0':'rgba(255,255,255,0.1)'} onMouseOut={e=>e.currentTarget.style.background=isLight?'#f1f5f9':'rgba(255,255,255,0.05)'}>{typeof tmpl === 'string' ? tmpl : tmpl.text}</button>
                                    <button onClick={() => editTemplate(tmpl)} title="Shablonni tahrirlash" aria-label="Shablonni tahrirlash" style={{ width: '32px', height: '32px', display: 'grid', placeItems: 'center', padding: 0, background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.14)', border: isLight ? '1px solid #bfdbfe' : '1px solid rgba(96,165,250,0.28)', borderRadius: '9px', color: isLight ? '#2563eb' : '#60a5fa', cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = isLight ? '#dbeafe' : 'rgba(59,130,246,0.28)'; }} onMouseLeave={e => { e.currentTarget.style.background = isLight ? '#eff6ff' : 'rgba(59,130,246,0.14)'; }}><FiEdit3 size={15} /></button>
                                    <button onClick={() => customConfirm(t('analytics.deleteTemplateConfirmTitle'), t('analytics.deleteTemplateConfirmMessage').replace('{name}', typeof tmpl === 'string' ? tmpl : tmpl.text), () => { setTemplates(p => p.filter(x => x.id !== tmpl.id)); showToast(t('analytics.toast.templateDeleted'), 'error'); })} title="Shablonni o'chirish" aria-label="Shablonni o'chirish" style={{ width: '32px', height: '32px', display: 'grid', placeItems: 'center', padding: 0, background: isLight ? '#fef2f2' : 'rgba(239,68,68,0.14)', border: isLight ? '1px solid #fecaca' : '1px solid rgba(248,113,113,0.28)', borderRadius: '9px', color: isLight ? '#dc2626' : '#f87171', cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = isLight ? '#fee2e2' : 'rgba(239,68,68,0.28)'; }} onMouseLeave={e => { e.currentTarget.style.background = isLight ? '#fef2f2' : 'rgba(239,68,68,0.14)'; }}><FiTrash2 size={15} /></button>
                          </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <textarea placeholder={t('analytics.newTemplatePlaceholder')} value={newLocalTemplate} onChange={e=>setNewLocalTemplate(e.target.value)} style={{ flex: 1, minHeight: '64px', padding: '8px 10px', borderRadius: '6px', border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#fff' : 'rgba(0,0,0,0.2)', color: isLight ? '#0f172a' : '#fff', outline: 'none', fontSize: '12px', lineHeight: 1.4, resize: 'vertical', fontFamily: 'inherit' }} />
                      <button onClick={createNewLocalTemplate} style={{ padding: '8px 12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>+</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {!isReadOnly && (
                <AnimatePresence mode="wait">
                  {addingCardColumn === day ? (
                    <motion.div key="inline-add" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} style={{ marginTop: '10px' }} onClick={e => e.stopPropagation()}>
                      <textarea
                        autoFocus value={newCardText} onChange={e => setNewCardText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitNewCard(day); } if (e.key === 'Escape') { setAddingCardColumn(null); setNewCardText(''); } }}
                        placeholder="Karta nomini kiriting..."
                        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '2px solid #3b82f6', background: isLight ? '#fff' : '#1e293b', color: isLight ? '#0f172a' : '#fff', resize: 'none', outline: 'none', fontSize: '14px', minHeight: '72px', fontFamily: 'inherit', lineHeight: '1.4' }}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                        <button onClick={() => submitNewCard(day)} style={{ padding: '7px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}>Qo'shish</button>
                        <button onClick={() => { setAddingCardColumn(null); setNewCardText(''); }} aria-label={t('analytics.buttons.cancel')} style={{ width: '30px', height: '30px', background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><FiX size={17} /></button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div key="add-btn" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px' }}>
                      <button onClick={() => addTask(day)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer', color: isLight ? '#64748b' : '#8b94a8', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s', padding: '5px 0' }} onMouseOver={(e) => e.currentTarget.style.color = isLight ? '#0f172a' : '#ffffff'} onMouseOut={(e) => e.currentTarget.style.color = isLight ? '#64748b' : '#8b94a8'}><span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span> {t('analytics.addCard')}</button>
                      <button onClick={(e) => { e.stopPropagation(); setActiveTemplateColumn(activeTemplateColumn === day ? null : day); }} style={{ width: '28px', height: '28px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.05)', color: isLight ? '#64748b' : '#8b94a8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg></button>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </motion.div>
            );
          })}
        </AnimatePresence>

        {!isReadOnly && <motion.div layout style={{ minWidth: '280px' }}>
          {isAddingColumn ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ background: isLight ? '#f8fafc' : '#272844', borderRadius: '12px', padding: '12px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)' }}>
              <input type="text" autoFocus placeholder={t('analytics.newColumnPlaceholder')} value={newColumnName} onChange={(e) => setNewColumnName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddColumnSubmit()} style={{ width: '100%', padding: '10px 15px', borderRadius: '8px', boxSizing: 'border-box', border: '2px solid #3b82f6', background: isLight ? '#fff' : 'rgba(255,255,255,0.05)', color: isLight ? '#0f172a' : '#fff', outline: 'none', marginBottom: '10px', fontSize: '14px' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button onClick={handleAddColumnSubmit} style={{ background: '#5b4b8a', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>{t('analytics.add')}</button>
                <button onClick={() => { setIsAddingColumn(false); setNewColumnName(''); }} style={{ background: 'transparent', border: 'none', color: '#8b94a8', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
            </motion.div>
          ) : (
            <button onClick={() => setIsAddingColumn(true)} style={{ width: '100%', height: '46px', borderRadius: '12px', border: isLight ? '1.5px dashed rgba(0,0,0,0.15)' : '1.5px dashed rgba(255,255,255,0.15)', background: 'transparent', color: isLight ? '#64748b' : '#8b94a8', display: 'flex', alignItems: 'center', padding: '0 15px', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.background = isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = isLight ? '#0f172a' : '#fff'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isLight ? '#64748b' : '#8b94a8'; }}>
              <span style={{ fontSize: '18px', fontWeight: '300' }}>+</span> {t('analytics.addList')}
            </button>
          )}
        </motion.div>}
      </div>

      {/* DRAWER (SHABLON / ARXIV) */}
      <AnimatePresence>
        {drawerContent && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={(e) => { e.stopPropagation(); setDrawerContent(null); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(5px)', zIndex: 40 }} />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: isMobile ? '100vw' : '400px', background: isLight ? '#ffffff' : '#1e293b', zIndex: 50, padding: isMobile ? '20px 16px' : '30px', overflowY: 'auto', boxShadow: '-5px 0 30px rgba(0,0,0,0.3)', borderLeft: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 'bold' }}>{drawerContent === 'templates' ? t('analytics.templates') : t('analytics.archives')}</h2>
                <button onClick={() => setDrawerContent(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: isLight ? '#64748b' : '#cbd5e1' }}>×</button>
              </div>

              {drawerContent === 'templates' && (
                <div>
                  <button onClick={createNewTemplate} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', background: '#3b82f6', color: '#fff', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer', marginBottom: '12px' }}>{t('analytics.createNewTemplate')}</button>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
{templates.length === 0 ? <p style={{ color: '#8b94a8', textAlign: 'center' }}>{t('analytics.noTemplates')}</p> : null}
                      {templates.map(temp => (
                      <div key={temp.id} style={{ padding: '15px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', flex: 1 }}>{temp.text}</span>
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button onClick={() => editTemplate(temp)} title="Shablonni tahrirlash" aria-label="Shablonni tahrirlash" style={{ width: '32px', height: '32px', display: 'grid', placeItems: 'center', padding: 0, background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.14)', border: isLight ? '1px solid #bfdbfe' : '1px solid rgba(96,165,250,0.28)', borderRadius: '9px', color: isLight ? '#2563eb' : '#60a5fa', cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = isLight ? '#dbeafe' : 'rgba(59,130,246,0.28)'; }} onMouseLeave={e => { e.currentTarget.style.background = isLight ? '#eff6ff' : 'rgba(59,130,246,0.14)'; }}><FiEdit3 size={15} /></button>
                          <button onClick={() => customConfirm(t('analytics.deleteTemplateConfirmTitle'), t('analytics.deleteTemplateConfirmMessage').replace('{name}', temp.text), () => { setTemplates(p => p.filter(x => x.id !== temp.id)); showToast(t('analytics.toast.templateDeleted'), 'error'); })} title="Shablonni o'chirish" aria-label="Shablonni o'chirish" style={{ width: '32px', height: '32px', display: 'grid', placeItems: 'center', padding: 0, background: isLight ? '#fef2f2' : 'rgba(239,68,68,0.14)', border: isLight ? '1px solid #fecaca' : '1px solid rgba(248,113,113,0.28)', borderRadius: '9px', color: isLight ? '#dc2626' : '#f87171', cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = isLight ? '#fee2e2' : 'rgba(239,68,68,0.28)'; }} onMouseLeave={e => { e.currentTarget.style.background = isLight ? '#fef2f2' : 'rgba(239,68,68,0.14)'; }}><FiTrash2 size={15} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {drawerContent === 'archives' && (
                <div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', padding: '5px', borderRadius: '10px' }}>
                    <button onClick={() => setArchiveTab('cards')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: archiveTab === 'cards' ? (isLight ? '#fff' : '#334155') : 'transparent', color: archiveTab === 'cards' ? (isLight ? '#0f172a' : '#fff') : '#8b94a8', fontWeight: 'bold', cursor: 'pointer', boxShadow: archiveTab === 'cards' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none' }}>{t('analytics.cardsTab')}</button>
                    <button onClick={() => setArchiveTab('columns')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: archiveTab === 'columns' ? (isLight ? '#fff' : '#334155') : 'transparent', color: archiveTab === 'columns' ? (isLight ? '#0f172a' : '#fff') : '#8b94a8', fontWeight: 'bold', cursor: 'pointer', boxShadow: archiveTab === 'columns' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none' }}>{t('analytics.columnsTab')}</button>
                  </div>
                  {archiveTab === 'cards' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {archives.cards.length === 0 ? <p style={{ color: '#8b94a8', textAlign: 'center' }}>{t('analytics.noArchiveCards')}</p> : null}
                      {archives.cards.map(card => (
                        <div key={card.id} style={{ padding: '15px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', borderRadius: '10px', borderLeft: '4px solid var(--accent-color)' }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '14px', fontWeight: '500' }}>{card.text}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#8b94a8' }}>{t('analytics.column')}: {boards.find(b=>b.id===card.board)?.title} | {t('analytics.list')}: {card.originalDay}</p>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={() => restoreCard(card)} style={{ flex: 1, padding: '8px', background: 'transparent', border: isLight ? '1px solid #cbd5e1' : '1px solid #475569', borderRadius: '6px', color: isLight ? '#0f172a' : '#fff', cursor: 'pointer', fontWeight: 'bold' }}>{t('analytics.restore')}</button>
                            <button onClick={() => customConfirm(t('analytics.cardDeleteConfirmTitle'), t('analytics.cardDeleteConfirmMessage'), () => { setArchives(prev => ({ ...prev, cards: prev.cards.filter(c => c.id !== card.id) })); showToast(t('analytics.toast.cardDeleted'), 'error'); })} title={t('analytics.buttons.delete')} aria-label={t('analytics.buttons.delete')} style={{ width: '34px', height: '34px', display: 'grid', placeItems: 'center', padding: 0, background: 'transparent', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', cursor: 'pointer' }}><FiTrash2 size={15} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {archiveTab === 'columns' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {archives.columns.length === 0 ? <p style={{ color: '#8b94a8', textAlign: 'center' }}>{t('analytics.noArchiveColumns')}</p> : null}
                      {archives.columns.map(col => (
                        <div key={col.id} style={{ padding: '15px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', borderRadius: '10px', borderLeft: '4px solid var(--accent-color)' }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '14px', fontWeight: 'bold' }}>{t('analytics.column')}: {col.name}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#8b94a8' }}>{t('analytics.board')}: {boards.find(b=>b.id===col.board)?.title} | {col.tasks?.length || 0} {t('analytics.tasksCount')}</p>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={() => restoreColumn(col)} style={{ flex: 1, padding: '8px', background: 'transparent', border: isLight ? '1px solid #cbd5e1' : '1px solid #475569', borderRadius: '6px', color: isLight ? '#0f172a' : '#fff', cursor: 'pointer', fontWeight: 'bold' }}>{t('analytics.restore')}</button>
                            <button onClick={() => customConfirm(t('analytics.columnDeleteConfirmTitle'), t('analytics.columnDeleteConfirmMessage'), () => { setArchives(prev => ({ ...prev, columns: prev.columns.filter(c => c.id !== col.id) })); showToast(t('analytics.toast.columnDeleted'), 'error'); })} title={t('analytics.buttons.delete')} aria-label={t('analytics.buttons.delete')} style={{ width: '34px', height: '34px', display: 'grid', placeItems: 'center', padding: 0, background: 'transparent', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', cursor: 'pointer' }}><FiTrash2 size={15} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ====================================================================== */}
      {/* 1:1 KARTOCHKA TAFSILOTLARI OYNASI (PREMIUM MODAL) */}
      {/* ====================================================================== */}
      <AnimatePresence>
        {openedCard && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setOpenedCard(null); setIsEditingDesc(false); setActivePopup(null); }} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(3px)' }} />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                position: 'relative', width: isMobile ? '100vw' : '900px', maxWidth: isMobile ? '100vw' : '95vw',
                height: isMobile ? '100dvh' : '85vh',
                background: isLight ? '#ffffff' : '#191929', color: isLight ? '#0f172a' : '#ffffff',
                borderRadius: isMobile ? '0' : '12px', boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
                overflow: isMobile ? 'auto' : 'hidden',
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)',
                fontFamily: 'Inter, sans-serif'
              }}
              onClick={() => setActivePopup(null)} 
            >
              
              {/* === CHAP TOMON === */}
              <div style={{ flex: isMobile ? '1 1 auto' : '1 1 0', minWidth: 0, padding: isMobile ? '16px 14px' : '30px 24px', display: 'flex', flexDirection: 'column', borderBottom: isMobile ? (isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)') : 'none', overflowY: 'auto' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={isReadOnly ? undefined : (e) => { e.stopPropagation(); setActivePopup(activePopup === 'status' ? null : 'status'); }}
                      style={{ background: statusColors[openedCard.card.status] || '#50516b', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: isReadOnly ? 'default' : 'pointer', transition: 'background 0.2s' }}
                    >
                      {openedCard.card.status} {!isReadOnly && <span style={{ fontSize: '10px' }}>▼</span>}
                    </button>
                    {activePopup === 'status' && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '8px', width: '180px', background: '#25263f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', boxShadow: '0 4px 15px rgba(0,0,0,0.3)', zIndex: 60, padding: '5px 0' }}>
                        {['Ochiq', 'Jarayon', 'Tekshiruvda', 'Tayyor'].map(status => (
                          <div key={status} onClick={() => handleStatusChange(status)} style={{ padding: '8px 15px', cursor: 'pointer', fontSize: '13px', color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }} onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                            <span style={{width: '10px', height: '10px', borderRadius: '50%', background: statusColors[status]}}></span> {status}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {!isReadOnly && <div style={{ position: 'relative' }} onMouseEnter={() => setHoveredIcon('template')} onMouseLeave={() => setHoveredIcon(null)}>
                      <button onClick={() => saveCardAsTemplate(openedCard.card)} style={{ width: '32px', height: '32px', borderRadius: '4px', background: 'transparent', border: 'none', color: '#8b94a8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg></button>
                      {hoveredIcon === 'template' && <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px', padding: '4px 8px', background: '#3b3d5c', color: '#fff', fontSize: '11px', borderRadius: '4px', whiteSpace: 'nowrap', zIndex: 70 }}>Shablon yaratish</div>}
                    </div>}
                    {!isReadOnly && <div style={{ position: 'relative' }} onMouseEnter={() => setHoveredIcon('archive')} onMouseLeave={() => setHoveredIcon(null)}>
                      <button onClick={() => archiveTask(openedCard.day, openedCard.card)} style={{ width: '32px', height: '32px', borderRadius: '4px', background: 'transparent', border: 'none', color: '#8b94a8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg></button>
                      {hoveredIcon === 'archive' && <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px', padding: '4px 8px', background: '#3b3d5c', color: '#fff', fontSize: '11px', borderRadius: '4px', whiteSpace: 'nowrap', zIndex: 70 }}>{t('analytics.labels.archiveTask')}</div>}
                    </div>}
                    <button onClick={() => { setOpenedCard(null); setIsEditingDesc(false); setActivePopup(null); }} style={{ width: '32px', height: '32px', borderRadius: '4px', background: 'transparent', border: 'none', color: '#8b94a8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>
                  </div>
                </div>

                {/* TEGLAR DISPLAY */}
                {openedCard.card.labels && openedCard.card.labels.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {openedCard.card.labels.map(labelId => {
                      const lbl = LABEL_COLORS.find(l => l.id === labelId);
                      return lbl ? <span key={labelId} style={{ height: '10px', minWidth: '48px', borderRadius: '5px', background: lbl.color, display: 'inline-block', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleLabel(labelId); }} title="O'chirish uchun bosing" /> : null;
                    })}
                  </div>
                )}

                {/* DOUBLE CLICK BILAN TAHRIRLANADIGAN SARLAVHA */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
                  <div title={getDeadlinePresentation(openedCard.card).overdue ? 'Muddat o\'tib ketgan' : openedCard.card.status} style={{ width: '18px', height: '18px', borderRadius: '50%', border: `3px solid ${getDeadlinePresentation(openedCard.card).color}`, flexShrink: 0, transition: 'border-color 0.3s' }}></div>
                  
                  {isEditingModalTitle ? (
                    <input 
                      autoFocus value={modalTitleText} onChange={(e) => setModalTitleText(e.target.value)}
                      onBlur={handleSaveModalTitle} onKeyDown={(e) => { if(e.key === 'Enter') handleSaveModalTitle(); }}
                      style={{ width: '100%', padding: '5px 10px', fontSize: '22px', fontWeight: 'bold', background: isLight ? '#f1f5f9' : '#1e293b', border: '2px solid #3b82f6', borderRadius: '8px', color: isLight ? '#0f172a' : '#fff', outline: 'none' }}
                    />
                  ) : (
                    <h2
                      onDoubleClick={isReadOnly ? undefined : (e) => { e.stopPropagation(); setModalTitleText(openedCard.card.text); setIsEditingModalTitle(true); }}
                      style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', letterSpacing: '0.2px', cursor: isReadOnly ? 'default' : 'text' }} title={isReadOnly ? undefined : t('analytics.tooltips.doubleClickHint')}
                    >
                      {openedCard.card.text}
                    </h2>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '35px', flexWrap: 'wrap' }}>

                  {/* MUDDAT — faqat ko'rish (isReadOnly) yoki tahrirlash */}
                  {(!isReadOnly || openedCard.card.deadline) && <div style={{ position: 'relative' }}>
                    <button
                      onClick={isReadOnly ? undefined : (e) => { e.stopPropagation(); openDatePopup(e); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: openedCard.card.deadline ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)') : 'transparent', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.15)', color: isLight ? '#475569' : '#cbd5e1', padding: '6px 14px', borderRadius: '16px', cursor: isReadOnly ? 'default' : 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg> 
                      {openedCard.card.deadline ? `${openedCard.card.deadline.date} ${openedCard.card.deadline.time}` : t('analytics.labels.dates')}
                    </button>
                    {activePopup === 'date' && (() => {
                      const now2 = new Date();
                      const todayFmt = `${String(now2.getDate()).padStart(2,'0')}.${String(now2.getMonth()+1).padStart(2,'0')}.${now2.getFullYear()}`;
                      const openedDateFmt = getCardOpenedDateString(openedCard.card);
                      const selectToday = () => {
                        if (todayFmt === openedDateFmt) return;
                        setCalViewDate(now2);
                        setSelDeadlineDate(todayFmt);
                      };
                      return (
                      <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', top: datePopupPosition?.top || 16, left: datePopupPosition?.left || 16, width: 'min(292px, calc(100vw - 32px))', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box', background: 'linear-gradient(145deg,#0f172a,#1e293b)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '18px', boxShadow: '0 24px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)', zIndex: 60, padding: '18px', backdropFilter: 'blur(20px)' }}>

                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '30px', height: '30px', borderRadius: '9px', background: 'rgba(59,130,246,0.18)', color: '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiCalendar size={16} /></div>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#f1f5f9' }}>Muddat belgilash</span>
                          </div>
                          <button onClick={() => setActivePopup(null)} style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1 }}>×</button>
                        </div>

                        {/* Month nav */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <button onClick={() => changeMonth(-1)} style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>‹</button>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#e2e8f0' }}>{monthNames[calViewDate.getMonth()]} {calViewDate.getFullYear()}</span>
                            <button onClick={selectToday} disabled={todayFmt === openedDateFmt} style={{ fontSize: '10px', fontWeight: 700, color: todayFmt === openedDateFmt ? '#64748b' : '#60a5fa', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '6px', padding: '2px 7px', cursor: todayFmt === openedDateFmt ? 'not-allowed' : 'pointer' }}>Bugun</button>
                          </div>
                          <button onClick={() => changeMonth(1)} style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>›</button>
                        </div>

                        {/* Calendar grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', textAlign: 'center', marginBottom: '14px' }}>
                          {['Du','Se','Cho','Pa','Ju','Sha','Ya'].map(d => (
                            <span key={d} style={{ fontSize: '10px', color: '#475569', fontWeight: 700, padding: '4px 0', display: 'block' }}>{d}</span>
                          ))}
                          {getCalendarDays().map((d, i) => {
                            const formattedDate = d ? `${String(d).padStart(2,'0')}.${String(calViewDate.getMonth()+1).padStart(2,'0')}.${calViewDate.getFullYear()}` : null;
                            const isSelected = selDeadlineDate === formattedDate;
                            const isToday = formattedDate === todayFmt;
                            const isOpenedDate = formattedDate === openedDateFmt;
                            return (
                              <span
                                key={i}
                                onClick={() => { if(d && !isOpenedDate) setSelDeadlineDate(formattedDate); }}
                                aria-disabled={isOpenedDate}
                                title={isOpenedDate ? 'Kartochka ochilgan kunni deadline qilib belgilab bo‘lmaydi' : undefined}
                                style={{
                                  cursor: d && !isOpenedDate ? 'pointer' : 'default',
                                  padding: '5px 0',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  fontWeight: isSelected || isToday ? 700 : 400,
                                  background: isSelected && !isOpenedDate ? '#3b82f6' : isOpenedDate ? 'rgba(239,68,68,0.12)' : isToday ? 'rgba(59,130,246,0.15)' : 'transparent',
                                  color: isOpenedDate ? '#f87171' : isSelected ? '#fff' : isToday ? '#60a5fa' : d ? '#cbd5e1' : 'transparent',
                                  border: isToday && !isSelected ? '1px solid rgba(59,130,246,0.35)' : '1px solid transparent',
                                  transition: 'all 0.12s',
                                  display: 'block',
                                }}
                              >{d || ""}</span>
                            );
                          })}
                        </div>

                        {/* Date + Time inputs */}
                        <div style={{ marginBottom: '14px' }}>
                          <div style={{ fontSize: '10px', color: '#475569', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '7px' }}>Sana va vaqt</div>
                          <div style={{ display: 'flex', gap: '8px', minWidth: 0, width: '100%' }}>
                            <input
                              type="text"
                              value={selDeadlineDate}
                              onChange={(e) => setSelDeadlineDate(e.target.value)}
                              placeholder="KK.OO.YYYY"
                              style={{ flex: '2 1 0', minWidth: 0, width: 0, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9', padding: '9px 11px', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                            />
                            <input
                              type="text"
                              inputMode="numeric"
                              value={selDeadlineTime}
                              onChange={(e) => setSelDeadlineTime(e.target.value)}
                              placeholder="18:00"
                              maxLength={5}
                              aria-label="Vaqt"
                              style={{ flex: '1 1 0', minWidth: 0, width: 0, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9', padding: '9px 8px', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', textAlign: 'center' }}
                            />
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {openedCard.card.deadline && (
                            <button
                              onClick={() => { updateCardDetails(openedCard.day, openedCard.card.id, { deadline: null }); showToast(t('analytics.messages.deadlineRemoved'), "info"); setActivePopup(null); }}
                              style={{ flex: 1, background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)', padding: '9px 6px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' }}
                            ><FiTrash2 size={14} /> O'chirish</button>
                          )}
                          <button
                            onClick={saveDeadline}
                            style={{ flex: 2, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', border: 'none', padding: '9px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(59,130,246,0.35)' }}
                          >✓ Saqlash</button>
                        </div>

                      </div>
                    );})()}
                  </div>}

                  {/* TEGLAR TUGMASI — isReadOnly bo'lsa yashiriladi */}
                  {!isReadOnly && <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setActivePopup(activePopup === 'labels' ? null : 'labels'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#cbd5e1', padding: '6px 14px', borderRadius: '16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                      Teglar
                    </button>
                    {activePopup === 'labels' && (
                      <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '100%', left: 0, marginTop: '10px', width: '220px', background: '#1e1f38', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', zIndex: 60, padding: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#fff' }}>Teglar</span>
                          <button onClick={() => setActivePopup(null)} style={{ background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer' }}>×</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          {LABEL_COLORS.map(lbl => {
                            const isSelected = (openedCard.card.labels || []).includes(lbl.id);
                            return (
                              <div key={lbl.id} onClick={() => toggleLabel(lbl.id)} style={{ height: '32px', borderRadius: '6px', background: lbl.color, cursor: 'pointer', border: isSelected ? '3px solid #fff' : '3px solid transparent', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border 0.15s' }}>
                                {isSelected && <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>}

                  {!isReadOnly && <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setActivePopup(activePopup === 'members' ? null : 'members'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#cbd5e1', padding: '6px 14px', borderRadius: '16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg> Mutaxasislar
                    </button>
                    {activePopup === 'members' && (
                      <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '100%', left: 0, marginTop: '6px', width: '220px', background: 'linear-gradient(145deg,#0f172a,#1e293b)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', boxShadow: '0 16px 40px rgba(0,0,0,0.6)', zIndex: 60, padding: '10px', backdropFilter: 'blur(16px)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#cbd5e1' }}>Mutaxasislar</span>
                          <button onClick={() => setActivePopup(null)} style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', lineHeight: 1 }}>×</button>
                        </div>
                        <input type="text" value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder="Qidirish..." style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', padding: '6px 10px', borderRadius: '8px', color: '#f1f5f9', fontSize: '12px', outline: 'none', marginBottom: '8px', fontFamily: 'inherit' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '200px', overflowY: 'auto' }}>
                          {allMembers.filter(u => u.name.toLowerCase().includes(memberSearch.toLowerCase())).map(u => {
                            const isSelected = openedCard.card.assigned?.includes(u.id);
                            return (
                              <div key={u.id} onClick={() => toggleMember(u.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '5px 6px', borderRadius: '8px', background: isSelected ? 'rgba(59,130,246,0.15)' : 'transparent', transition: 'background 0.12s' }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: u.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                                  {u.name.charAt(0).toUpperCase()}
                                </div>
                                <span style={{ fontSize: '12px', fontWeight: 500, flex: 1, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                                {isSelected && <span style={{ color: '#10b981', fontSize: '13px', fontWeight: 800, flexShrink: 0 }}>✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>}

                  {openedCard.card.assigned && openedCard.card.assigned.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {openedCard.card.assigned.map(id => {
                        const u = allMembers.find(m => m.id === id);
                        return u ? (
                          <div key={u.id} title={u.name} style={{ width: '28px', height: '28px', borderRadius: '50%', background: u.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', color: '#fff', marginLeft: '-8px', border: `2px solid ${isLight ? '#ffffff' : '#191929'}` }}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                {/* OPISANIE (EDITOR VA LINKLAR) */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b94a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>{t('analytics.labels.description')}</h3>
                    </div>
                  </div>
                  <div style={{ width: '40px', height: '3px', background: '#3b82f6', borderRadius: '3px', marginBottom: '15px', marginLeft: '28px' }}></div>
                  
                  <div 
                    onClick={(e) => handleEditorClick(e)}
                    style={{ border: isEditingDesc ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.05)', borderRadius: '8px', overflow: 'hidden', background: isEditingDesc ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.03)', transition: 'all 0.2s' }}
                  >
                    {isEditingDesc && (
                      <div style={{ display: 'flex', gap: '15px', padding: '10px 15px', borderBottom: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)' }}>
                        <span onMouseDown={(e) => { e.preventDefault(); execCmd('bold'); }} style={{ cursor: 'pointer', fontWeight: 'bold', color: '#8b94a8' }}>B</span>
                        <span onMouseDown={(e) => { e.preventDefault(); execCmd('italic'); }} style={{ cursor: 'pointer', fontStyle: 'italic', color: '#8b94a8' }}>I</span>
                        <span onMouseDown={(e) => { e.preventDefault(); execCmd('underline'); }} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#8b94a8' }}>U</span>
                        <span onMouseDown={(e) => { e.preventDefault(); execCmd('insertUnorderedList'); }} style={{ cursor: 'pointer', color: '#8b94a8', display: 'grid', placeItems: 'center' }} title="Ro'yxat"><FiList size={16} /></span>
                        <span onMouseDown={(e) => { e.preventDefault(); execCmd('insertOrderedList'); }} style={{ cursor: 'pointer', color: '#8b94a8' }}>½</span>
                        <span onMouseDown={(e) => { e.preventDefault(); handleLinkInsertion(); }} style={{ cursor: 'pointer', color: '#3b82f6', display: 'grid', placeItems: 'center' }} title={t('analytics.insertLink')}><FiLink2 size={16} /></span>
                        <span onMouseDown={(e) => { e.preventDefault(); execCmd('justifyLeft'); }} style={{ cursor: 'pointer', color: '#8b94a8' }}>≡L</span>
                        <span onMouseDown={(e) => { e.preventDefault(); execCmd('justifyCenter'); }} style={{ cursor: 'pointer', color: '#8b94a8' }}>≡C</span>
                      </div>
                    )}
                    
                    <div 
                      ref={editorRef}
                      contentEditable={isEditingDesc}
                      suppressContentEditableWarning={true}
                      data-placeholder={t('analytics.messages.descriptionPlaceholder')}
                      className="editable-description"
                      onClick={(e) => {
                        if (!isEditingDesc) {
                          const anchor = e.target && e.target.closest ? e.target.closest('a') : null;
                          if (anchor) {
                            e.preventDefault();
                            openExternalLink(anchor.getAttribute('href') || anchor.href);
                          }
                        }
                      }}
                      style={{ 
                        width: '100%', minHeight: '100px', padding: '15px', color: isLight ? '#0f172a' : '#cbd5e1', 
                        outline: 'none', fontSize: '14px', lineHeight: '1.6', cursor: isEditingDesc ? 'text' : 'pointer', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'
                      }}
                    />

                    {isEditingDesc && (
                      <div style={{ padding: '10px 15px', display: 'flex', gap: '15px', alignItems: 'center', justifyContent: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}>
                        <button onClick={(e) => { e.stopPropagation(); handleSaveDescription(); }} style={{ background: '#5b4b8a', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}>{t('analytics.buttons.save')}</button>
                        <button onClick={(e) => { e.stopPropagation(); setIsEditingDesc(false); if(editorRef.current) editorRef.current.innerHTML = openedCard.card.description || ""; }} style={{ background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', fontSize: '13px' }}>{t('analytics.buttons.cancel')}</button>
                      </div>
                    )}
                  </div>
                  <style>{`
                    .editable-description:empty:before { content: attr(data-placeholder); color: #8b94a8; }
                    .editable-description a,
                    .editable-description a:visited {
                      color: #60a5fa !important;
                      text-decoration: underline !important;
                      text-underline-offset: 3px;
                      cursor: pointer;
                      font-weight: 600;
                      transition: color 0.2s ease, opacity 0.2s ease;
                    }
                    .editable-description a:hover,
                    .editable-description a:focus {
                      color: #93c5fd !important;
                      opacity: 1;
                    }
                  `}</style>
                </div>

                {/* === CHECKLIST === */}
                {openedCard.card.checklist && openedCard.card.checklist.length > 0 && (
                  <div style={{ marginTop: '28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b94a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Checklist</h3>
                      {(() => {
                        const done = openedCard.card.checklist.filter(i => i.done).length;
                        const total = openedCard.card.checklist.length;
                        return <span style={{ fontSize: '12px', color: '#8b94a8' }}>({done}/{total})</span>;
                      })()}
                    </div>
                    {(() => {
                      const done = openedCard.card.checklist.filter(i => i.done).length;
                      const total = openedCard.card.checklist.length;
                      const pct = total ? Math.round(done / total * 100) : 0;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                          <span style={{ fontSize: '12px', color: pct === 100 ? '#10b981' : '#8b94a8', minWidth: '30px', textAlign: 'right' }}>{pct}%</span>
                          <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#10b981' : '#3b82f6', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {openedCard.card.checklist.map(item => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)' }}>
                          <input type="checkbox" checked={item.done} onChange={() => !isReadOnly && toggleChecklistItem(openedCard.day, openedCard.card.id, item.id)} onClick={e => e.stopPropagation()} disabled={isReadOnly} style={{ cursor: isReadOnly ? 'default' : 'pointer', accentColor: '#3b82f6', width: '16px', height: '16px', flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: '13px', textDecoration: item.done ? 'line-through' : 'none', color: item.done ? '#8b94a8' : (isLight ? '#0f172a' : '#e2e8f0') }}>{item.text}</span>
                          {!isReadOnly && <button onClick={(e) => { e.stopPropagation(); removeChecklistItem(openedCard.day, openedCard.card.id, item.id); }} aria-label={t('analytics.buttons.delete')} style={{ width: '28px', height: '28px', background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', padding: 0, opacity: 0.7, display: 'grid', placeItems: 'center' }}><FiX size={15} /></button>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Checklist element qo'shish tugmasi */}
                {!isReadOnly && <div style={{ marginTop: openedCard.card.checklist && openedCard.card.checklist.length > 0 ? '12px' : '28px' }} onClick={e => e.stopPropagation()}>
                  {activePopup === 'checklist' ? (
                    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        autoFocus type="text" value={newChecklistText} onChange={e => setNewChecklistText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addChecklistItem(openedCard.day, openedCard.card.id); if (e.key === 'Escape') { setActivePopup(null); setNewChecklistText(''); } }}
                        placeholder="Element nomini kiriting..."
                        style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #3b82f6', background: isLight ? '#fff' : 'rgba(255,255,255,0.05)', color: isLight ? '#0f172a' : '#fff', outline: 'none', fontSize: '13px' }}
                      />
                      <button onClick={() => addChecklistItem(openedCard.day, openedCard.card.id)} style={{ padding: '8px 14px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>+</button>
                      <button onClick={() => { setActivePopup(null); setNewChecklistText(''); }} aria-label={t('analytics.buttons.cancel')} style={{ width: '34px', height: '34px', background: 'none', border: 'none', color: '#8b94a8', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><FiX size={17} /></button>
                    </motion.div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setActivePopup('checklist'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', color: isLight ? '#475569' : '#8b94a8', padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                      + Checklist element
                    </button>
                  )}
                </div>}

              </div>

              {/* === TARIX VA IZOHLAR === */}
              <div style={{ flex: isMobile ? '0 0 auto' : '0 0 340px', minHeight: isMobile ? '300px' : 0, maxHeight: isMobile ? '45dvh' : 'none', background: isLight ? '#f8fafc' : '#141423', padding: isMobile ? '14px' : '30px 24px', display: 'flex', flexDirection: 'column', overflowY: 'auto', borderLeft: isMobile ? 'none' : (isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.05)') }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b94a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 'bold' }}>{t('analytics.labels.commentsEvents')}</h3>
                </div>

                {!isReadOnly && <div style={{ position: 'relative', marginBottom: '30px' }} onClick={(e) => e.stopPropagation()}>
                  <textarea
                    value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => { if((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleAddComment(); }} placeholder={t('analytics.messages.commentPlaceholder')}
                    style={{ width: '100%', height: '80px', padding: '12px', paddingBottom: '40px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', outline: 'none', resize: 'none', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                  <div style={{ position: 'absolute', bottom: '10px', left: '12px', right: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#8b94a8' }}>Ctrl/⌘ + Enter</span>
                    <button onClick={handleAddComment} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }} onMouseOver={e=>e.currentTarget.style.background='#059669'} onMouseOut={e=>e.currentTarget.style.background='#10b981'}>{t('analytics.buttons.send')}</button>
                  </div>
                </div>}

                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', paddingRight: '5px' }}>
                  {Array.isArray(openedCard.card.history) ? (openedCard.card.history || []).map((ev, idx) => {
                    const author = typeof ev.author === 'string' && ev.author.trim() ? ev.author : 'Noma\'lum';
                    const isComment = ev.type === 'comment';
                    const memberColor = allMembers.find(m => m.name === author)?.color;
                    const avatarColor = memberColor || (isComment ? '#8b5cf6' : '#3b82f6');
                    return (
                      <div key={ev.id || `history-${idx}`} style={{ display: 'flex', gap: '10px', background: isComment ? 'rgba(139,92,246,0.06)' : 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '10px', border: isComment ? '1px solid rgba(139,92,246,0.15)' : '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: avatarColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '13px', flexShrink: 0 }}>
                          {author.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 'bold', fontSize: '13px', color: avatarColor }}>{author}</span>
                            <span style={{ background: isComment ? 'rgba(139,92,246,0.2)' : 'rgba(59,130,246,0.2)', color: isComment ? '#a78bfa' : '#60a5fa', border: isComment ? '1px solid rgba(139,92,246,0.3)' : '1px solid rgba(59,130,246,0.3)', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', fontWeight: '600' }}>
                              {isComment ? 'Izoh' : 'Hodisa'}
                            </span>
                          </div>
                          <p style={{ margin: '0 0 6px 0', fontSize: '13px', lineHeight: '1.4', color: '#cbd5e1', wordBreak: 'break-word' }}>{ev.text || ''}</p>
                          <span style={{ fontSize: '11px', color: '#8b94a8' }}>{ev.date || ''}</span>
                        </div>
                      </div>
                    );
                  }) : null}
                  {(!openedCard.card.history || openedCard.card.history.length === 0) && (
                    <p style={{ textAlign: 'center', color: '#8b94a8', fontSize: '13px' }}>Hozircha hodisalar yo'q</p>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* GLOBAL MODALLAR */}
      <AnimatePresence>
        {alertModal.isOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)' }} />
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} style={{ position: 'relative', background: isLight ? '#ffffff' : '#1e293b', padding: '24px', borderRadius: '16px', width: isMobile ? 'calc(100vw - 32px)' : '320px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 'bold' }}>{alertModal.title}</h3>
              <p style={{ margin: '0 0 25px 0', fontSize: '14px', color: '#8b94a8', lineHeight: '1.5' }}>{alertModal.message}</p>
              <button onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Tushundim</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmModal.isOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} />
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} style={{ position: 'relative', background: isLight ? '#ffffff' : '#1e293b', padding: '24px', borderRadius: '16px', width: isMobile ? 'calc(100vw - 32px)' : '320px', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 'bold' }}>{confirmModal.title}</h3>
              <p style={{ margin: '0 0 25px 0', fontSize: '14px', color: '#8b94a8', lineHeight: '1.5' }}>{confirmModal.message}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', color: isLight ? '#0f172a' : '#fff', cursor: 'pointer', fontWeight: 'bold' }}>{t('analytics.buttons.cancel')}</button>
                <button onClick={() => { confirmModal.onConfirm(); setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null }); }} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: confirmModal.isDanger ? '#ef4444' : '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>{confirmModal.isDanger ? t('analytics.buttons.delete') : t('analytics.buttons.confirm')}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {promptModal.isOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closePromptModal} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} />
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} style={{ position: 'relative', background: isLight ? '#ffffff' : '#1e293b', padding: '24px', borderRadius: '16px', width: isMobile ? 'calc(100vw - 32px)' : '320px', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold' }}>{promptModal.title}</h3>
              <textarea
                autoFocus placeholder={promptModal.placeholder} value={promptModal.value}
                onChange={e => setPromptModal(p => ({ ...p, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { promptModal.onConfirm(promptModal.value, promptModal.description); closePromptModal(); } }}
                style={{ width: '100%', minHeight: '120px', boxSizing: 'border-box', padding: '10px 15px', borderRadius: '8px', border: '2px solid #3b82f6', background: isLight ? '#fff' : 'rgba(0,0,0,0.2)', color: isLight ? '#0f172a' : '#fff', outline: 'none', marginBottom: '20px', fontSize: '14px', lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }}
              />
              {promptModal.isTemplate && (
                <textarea
                  placeholder="Shablon tarifini yozing..."
                  value={promptModal.description}
                  onChange={e => setPromptModal(p => ({ ...p, description: e.target.value }))}
                  style={{ width: '100%', minHeight: '120px', boxSizing: 'border-box', padding: '10px 15px', borderRadius: '8px', border: '1px solid #64748b', background: isLight ? '#fff' : 'rgba(0,0,0,0.2)', color: isLight ? '#0f172a' : '#fff', outline: 'none', marginBottom: '20px', fontSize: '14px', lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }}
                />
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={closePromptModal} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', color: isLight ? '#0f172a' : '#fff', cursor: 'pointer', fontWeight: 'bold' }}>{t('analytics.buttons.cancel')}</button>
                <button onClick={() => { promptModal.onConfirm(promptModal.value, promptModal.description); closePromptModal(); }} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>{promptModal.actionLabel || t('analytics.buttons.save')}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div style={{ position: 'fixed', top: '24px', left: 0, right: 0, zIndex: 9999999, height: '54px', pointerEvents: 'none' }}>
        <AnimatePresence>
          {toasts.map(toast => {
            const toastStyle = toast.type === 'success'
              ? { accent: '#34d399', icon: <FiCheckCircle />, background: 'rgba(16,185,129,0.18)', border: 'rgba(52,211,153,0.35)' }
              : toast.type === 'error'
                ? { accent: '#fda4af', icon: <FiAlertCircle />, background: 'rgba(244,63,94,0.18)', border: 'rgba(253,164,175,0.35)' }
                : toast.type === 'warning'
                  ? { accent: '#fcd34d', icon: <FiAlertTriangle />, background: 'rgba(245,158,11,0.18)', border: 'rgba(252,211,77,0.35)' }
                  : { accent: '#93c5fd', icon: <FiInfo />, background: 'rgba(59,130,246,0.18)', border: 'rgba(147,197,253,0.35)' };
            return (
              <motion.div key={toast.id} initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }} transition={{ duration: 0.18, ease: 'easeOut' }} style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 'max-content', maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', background: toastStyle.background, color: '#f8fafc', padding: '10px 14px 10px 10px', borderRadius: '12px', boxShadow: '0 14px 34px rgba(15,23,42,0.22)', border: `1px solid ${toastStyle.border}`, backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px', fontWeight: '600', letterSpacing: '0.1px' }}>
                <span style={{ width: '28px', height: '28px', flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '9px', background: 'rgba(255,255,255,0.14)', color: toastStyle.accent }}>{toastStyle.icon}</span><span style={{ lineHeight: 1.35 }}>{toast.message}</span>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>

    </motion.div>
  );
};

export default Analytics;